import { Check, Close, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import {
  BodyVM,
  FilterGeneralityVM,
  SubscriptionAccordionGenerality,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';
import FormGenerality from '../../shared/generality/form';
import AccordionFilter from './accordion-filter';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { DateTime } from 'luxon';
import { HeadCell } from 'shell/components/shared/table/types';
import Table from 'shell/components/shared/table';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { noop } from 'shell/utils/noop';
import { AxiosResponse } from 'axios';

export const headCells: HeadCell<BodyVM, keyof BodyVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'idAnpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: '',
  },
];

interface AccordionGeneralityProps {
  disabled?: boolean;
  isEditMode?: boolean;
  isCohabitation?: boolean;
  isAire?: boolean;
  isSubscription?: boolean;
}

const AccordionGeneralityComponent = ({
  disabled = false,
  isCohabitation = false,
  isAire = false,
  isSubscription = false,
}: AccordionGeneralityProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const getPerson = useHttpClient(`${__PERSON_NEW_API_URL__}/persona`, {
    options: {
      method: 'POST',
    },
  });

  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        [key]: value,
      });
    },
    [formContext.values.generality, formContext.setFieldValue]
  );

  const onChangeValues = useCallback(
    (values) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        ...(values || {}),
      });
    },
    [formContext.values.generality, formContext.setFieldValue]
  );

  const onFieldPersonChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('person', {
        ...formContext.values.person,
        [key]: value,
      });
    },
    [formContext.values.person, formContext.setFieldValue]
  );

  const resetForm = useCallback(() => {
    formContext.setValues({
      ...formContext.values,
      person: {},
      generality: {},
    });
  }, [formContext.values, formContext.setValues]);

  const errorGeneralityPerson = useMemo(() => {
    return !!formContext.errors.person || !!formContext.errors.generality;
  }, [formContext.errors.person, formContext.errors.generality]);

  const onChangeFilter = useEventCallback(
    (key: keyof FilterGeneralityVM, value: FilterGeneralityVM[keyof FilterGeneralityVM]) => {
      formContext.setFieldValue('generality', {
        ...formContext.values.generality,
        filter: {
          ...(formContext.values.generality?.filter || {}),
          [key]: value,
        },
      });
    }
  );

  const onChangesFilter = useEventCallback((values: FilterGeneralityVM) => {
    formContext.setFieldValue('generality', {
      ...formContext.values.generality,
      filter: {
        ...(formContext.values.generality?.filter || {}),
        ...values,
      },
    });
  });

  const onSearch = useEventCallback(() => {
    const data = {
      criteriRicerca: {
        codiceFiscale: formContext.values.generality?.filter?.fiscalCode || undefined,
        cognome: formContext.values.generality?.filter?.surname || undefined,
        nome: formContext.values.generality?.filter?.name || undefined,
        senzaCognome: formContext.values.generality?.filter?.withoutSurname ? '1' : undefined,
        senzaNome: formContext.values.generality?.filter?.withoutName ? '1' : undefined,
      },
      datiRichiesta: {
        ricercaNazionale: '1',
      },
    };

    (
      getPerson({ data }) as unknown as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        ResponseAnprData
      >
    ).then((response) => {
      const castedResponse = (response as unknown as AxiosResponse<HttpResponse<ResponseAnprData>>)
        ?.data?.payload?.risposta3002OK;

      const datiSoggetto = castedResponse.listaSoggetti.datiSoggetto || [];

      if (datiSoggetto.length === 0) {
        // nessun soggetto trovato
        return;
      }

      if (datiSoggetto?.length > 1) {
        // più soggetti trovati

        formContext.setValues({
          ...(formContext.values || {}),
          generality: {
            filter: undefined,
            fromResearch: false,
            tablePersons: {
              body: datiSoggetto.map((person) => {
                let birthplace = '';
                // birthplace check for object
                if (person.generalita?.luogoNascita?.localita) {
                  birthplace = `${person.generalita?.luogoNascita?.localita.descrizioneLocalita} (${person.generalita?.luogoNascita?.localita.descrizioneStato})`;
                }

                if (person.generalita?.luogoNascita?.comune) {
                  birthplace = `${person.generalita?.luogoNascita?.comune.nomeComune} (${person.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`;
                }

                // Residence check for object
                let residence = '';

                const residenceArray = person.residenza || [];

                // scelgo il primo o è un array?
                if (residenceArray[0]?.indirizzo) {
                  residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} (${
                    residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''
                  })`;
                }
                // scelgo il primo o è un array?
                if (residenceArray[0]?.localitaEstera) {
                  residence = `${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneLocalita} (${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneStato})`;
                }

                let state = '';
                // state check for object
                if (residenceArray[0]?.indirizzo?.toponimo) {
                  const toponym = residenceArray[0]?.indirizzo?.toponimo;

                  const civic = residenceArray[0]?.indirizzo?.numeroCivico;

                  state = `${toponym.specie} ${toponym.denominazioneToponimo},${civic?.numero}${
                    civic?.esponente1 ? `/${civic.esponente1}${civic.lettera}` : ''
                  }`;
                }

                if (person.generalita?.soggettoAIRE && person.generalita?.soggettoAIRE === 'S') {
                  state = 'Iscritto AIRE';
                }

                if (
                  person.datiCancellazione &&
                  person.datiCancellazione?.descrizioneMotivoCancellazione
                ) {
                  state = `${person.datiCancellazione.descrizioneMotivoCancellazione}`;
                }

                return {
                  fiscalCode: person.generalita?.codiceFiscale?.codFiscale || '',
                  surname: person.generalita?.cognome || '',
                  name: person.generalita?.nome || '',
                  gender: person.generalita?.sesso || '',
                  birthday: person.generalita?.dataNascita
                    ? DateTime.fromISO(person.generalita.dataNascita).toFormat('dd/MM/yyyy')
                    : '',
                  birthplace,
                  residence,
                  state,
                  idAnpr: person.generalita?.idSchedaSoggettoANPR || '',
                  hidden: {
                    delete: true,
                    edit: true,
                  },
                };
              }),
            },
          },
        });

        return;
      }

      const formattedGenerality = transformGenerality(datiSoggetto[0]) || {};

      // un sono soggetto trovato
      formContext.setValues({
        ...(formContext.values || {}),
        generality: {
          fromResearch: true,
          filter: undefined,
          tablePersons: undefined,
          ...formattedGenerality,
        },
        person: {
          name: formattedGenerality.name || '',
          surname: formattedGenerality.surname || '',
          withoutName: formattedGenerality.withoutName || false,
          withoutSurname: formattedGenerality.withoutSurname || false,
          fiscalCode: formattedGenerality.fiscalCode || '',
        },
      });
    });
  });

  const onChangeRowsPerPage = useEventCallback((page: number) => {
    formContext.setValues({
      ...(formContext.values || {}),
      generality: {
        ...formContext.values.generality,
        tablePersons: {
          ...formContext.values.generality?.tablePersons,
          page,
        },
      },
    });
  });

  const onChangePage = useEventCallback((rowsPerPage: number) => {
    formContext.setValues({
      ...(formContext.values || {}),
      generality: {
        ...formContext.values.generality,
        tablePersons: {
          ...formContext.values.generality?.tablePersons,
          page: 0,
          rowsPerPage,
        },
      },
    });
  });

  const onVisibilityPress = useEventCallback((cell: BodyVM) => {
    const data = {
      criteriRicerca: {
        codiceFiscale: cell.fiscalCode || undefined,
        idSchedaSoggettoANPR: cell.idAnpr || undefined,
      },
      datiRichiesta: {
        ricercaNazionale: '1',
      },
    };

    (
      getPerson({ data }) as unknown as ResponsePromise<
        HttpResponse<ResponseAnprData>,
        ResponseAnprData
      >
    ).then((response) => {
      const castedResponse = (response as unknown as AxiosResponse<HttpResponse<ResponseAnprData>>)
        ?.data?.payload?.risposta3002OK;

      const datiSoggetto = castedResponse.listaSoggetti.datiSoggetto || [];

      if (datiSoggetto.length === 0) {
        return;
      }

      const formattedGenerality = transformGenerality(datiSoggetto[0]) || {};

      // un sono soggetto trovato
      formContext.setValues({
        ...(formContext.values || {}),
        generality: {
          fromResearch: true,
          filter: undefined,
          tablePersons: undefined,
          ...formattedGenerality,
        },
        person: {
          name: formattedGenerality.name || '',
          surname: formattedGenerality.surname || '',
          withoutName: formattedGenerality.withoutName || false,
          withoutSurname: formattedGenerality.withoutSurname || false,
          fiscalCode: formattedGenerality.fiscalCode || '',
        },
      });
    });
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-generality-content"
          id="panel-generality-header"
          expandIcon={<ExpandMore />}
        >
          {errorGeneralityPerson ? <Close color="error" /> : <Check color="success" />}
          <Typography>Generalità</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionFilter
            values={formContext.values.generality?.filter}
            onChange={onChangeFilter}
            onChangeFields={onChangesFilter}
            onSearch={onSearch}
          />
          {formContext.values.generality?.tablePersons?.body ? (
            <div style={{ padding: '30px 0 30px 0' }}>
              <Table
                ariaLabel="Lista Persone"
                page={formContext.values.generality?.tablePersons?.page || 0}
                rowsPerPage={formContext.values.generality?.tablePersons?.rowsPerPage}
                body={(formContext.values.generality?.tablePersons?.body || []) as Array<BodyVM>}
                count={formContext.values.generality?.tablePersons?.body?.length || 0}
                head={headCells}
                onSort={noop}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                orders={{}}
                isNotLayoutFixed
                noSort
                onVisibilityPress={onVisibilityPress}
                //hideButtons
              />
            </div>
          ) : undefined}
          <FormGenerality
            isCohabitation={isCohabitation}
            disabled={disabled || formContext.values.generality?.fromResearch}
            onChangePerson={onFieldPersonChange}
            onChangeGenerality={onFieldGeneralityChange}
            onChangeValues={onChangeValues}
            person={formContext.values.person}
            generality={formContext.values.generality}
            errorsGenerality={
              formContext.errors.generality as FormikErrors<SubscriptionAccordionGenerality>
            }
            errorsPerson={formContext.errors.person as FormikErrors<AccordionPersonForm>}
            touchedPerson={
              formContext.touched.person as unknown as FormikTouched<AccordionPersonForm>
            }
            touchedGenerality={
              formContext.touched
                .generality as unknown as FormikTouched<SubscriptionAccordionGenerality>
            }
            resetForm={resetForm}
            isAire={isAire}
            isDeathPerson={isSubscription}
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionGeneralityComponent.displayName = 'Accordion generality component';

const AccordionGeneralityResearch = memo(AccordionGeneralityComponent);

export default AccordionGeneralityResearch;
